import { useState } from 'react';
import api from '../../services/api';
import { Task } from './types';

interface TaskFormData {
  name: string;
  description: string;
  goalId: number | null;
  categoryId: number | null;
  energy: '1' | '2' | '3';  // 1=А, 2=Б, 3=В
  sprint: string;
  dueDate: string;
  dueTime: string;
}

export const useTaskOperations = (loadData: () => Promise<any>) => {
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [selectedGoal, setSelectedGoal] = useState<number | null | 'none'>(null);
  const [filterEnergy, setFilterEnergy] = useState<string>('all');
  const [filterSprint, setFilterSprint] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'archived'>('active');
  const [filterCategory, setFilterCategory] = useState<number | 'all'>('all');
  
  const [taskForm, setTaskForm] = useState<TaskFormData>({
    name: '',
    description: '',
    goalId: null,
    categoryId: null,
    energy: '2',
    sprint: '',
    dueDate: '',
    dueTime: ''
  });

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const taskData = {
        ...taskForm,
        goalId: taskForm.goalId || null,
        categoryId: taskForm.categoryId || null,
        sprint: taskForm.sprint || null,
        dueDate: taskForm.dueDate || null,
        dueTime: taskForm.dueTime || null
      };
      await api.post('/api/tasks', taskData);
      setTaskForm({ name: '', description: '', goalId: null, categoryId: null, energy: '2', sprint: '', dueDate: '', dueTime: '' });
      setShowTaskForm(false);
      await loadData();
    } catch (error: any) {
      console.error('Failed to create task:', error);
      alert(`Ошибка при создании задачи: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleUpdateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTaskId) return;

    try {
      const taskData = {
        ...taskForm,
        goalId: taskForm.goalId || null,
        categoryId: taskForm.categoryId || null,
        sprint: taskForm.sprint || null,
        dueDate: taskForm.dueDate || null,
        dueTime: taskForm.dueTime || null,
        // Если убрали цель, то автоматически убираем stepId (дело не может быть в шаге без цели)
        stepId: taskForm.goalId ? undefined : null
      };
      await api.put(`/api/tasks/${editingTaskId}`, taskData);
      setTaskForm({ name: '', description: '', goalId: null, categoryId: null, energy: '2', sprint: '', dueDate: '', dueTime: '' });
      setShowTaskForm(false);
      setEditingTaskId(null);
      await loadData();
    } catch (error: any) {
      console.error('Failed to update task:', error);
      alert(`Ошибка при обновлении задачи: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleEditTask = (task: Task) => {
    setEditingTaskId(task.id);
    setTaskForm({
      name: task.name,
      description: task.description || '',
      goalId: task.goalId,
      categoryId: task.categoryId || null,
      energy: task.energy,
      sprint: task.sprint || '',
      dueDate: task.dueDate || '',
      dueTime: task.dueTime || ''
    });
    // Не открываем верхнюю форму - редактирование теперь inline на карточке
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
    setTaskForm({ name: '', description: '', goalId: null, categoryId: null, energy: '2', sprint: '', dueDate: '', dueTime: '' });
    setShowTaskForm(false);
  };

  const handleDeleteTask = async (taskId: number) => {
    if (window.confirm('Удалить эту задачу?')) {
      try {
        await api.delete(`/api/tasks/${taskId}`);
        await loadData();
      } catch (error) {
        console.error('Failed to delete task:', error);
      }
    }
  };

  const handleUpdateTaskStatus = async (
    taskId: number, 
    newStatus: Task['status'], 
    completedAt?: string | null
  ) => {
    try {
      const updateData: any = { status: newStatus };
      
      // Если переводим в done или archived, сохраняем дату завершения
      if (newStatus === 'done' || newStatus === 'archived') {
        updateData.completedAt = completedAt || new Date().toISOString();
      }
      
      // Если возвращаем из архива, очищаем completedAt
      if (newStatus !== 'done' && newStatus !== 'archived') {
        updateData.completedAt = null;
      }
      
      await api.put(`/api/tasks/${taskId}`, updateData);
      // НЕ вызываем loadData() здесь - пусть вызывающий код сам решает когда перезагружать
      // Это избегает двойной загрузки при drag-and-drop
    } catch (error) {
      console.error('Failed to update task:', error);
      // При ошибке обязательно перезагружаем для отката изменений
      await loadData();
    }
  };

  const handleReorderTasks = async (taskIds: number[]) => {
    try {
      const { tasksAPI } = await import('../../services/api');
      await tasksAPI.reorder(taskIds);
      // Не перезагружаем данные - порядок уже изменен локально через drag-and-drop
    } catch (error) {
      console.error('Failed to reorder tasks:', error);
      // При ошибке перезагружаем данные чтобы вернуть старый порядок
      await loadData();
    }
  };

  return {
    showTaskForm,
    setShowTaskForm,
    editingTaskId,
    setEditingTaskId,
    selectedGoal,
    setSelectedGoal,
    filterEnergy,
    setFilterEnergy,
    filterSprint,
    setFilterSprint,
    filterStatus,
    setFilterStatus,
    filterCategory,
    setFilterCategory,
    taskForm,
    setTaskForm,
    handleCreateTask,
    handleUpdateTask,
    handleEditTask,
    handleCancelEdit,
    handleDeleteTask,
    handleUpdateTaskStatus,
    handleReorderTasks
  };
};
